import styled from "@emotion/styled";
import { LocalDate, LocalDateTime, Month } from "@js-joda/core";
import classNames from "classnames";
import React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import { Geo, NearFutureCaregiver } from "../messages";
import { AgencyId, PatientDocumentTypeId } from "../messages/ObjectIds";
import { PatientTaskInstance } from "../messages/PatientTasks";
import { STRINGS } from "../strings";
import { Patient } from "../system/AppState/AppPatientsState";
import { decimalHourToDisplay, minutesToDisplayTime } from "../time";
import { translate } from "../translate";
import { getFullName } from "../utils/getFullName";
import { extractPatientContacts } from "../utils/patientUtils";
import { MAP_MARKER_DUOTONE_ICON } from "../utils/svgs_duotone";
import { withDefault } from "../utils/withDefault";
import Alert from "./Alert";
import ContactsListCard from "./ContactsListCard";
import { AppCard, AppCardContent } from "./Generic/AppCard";
import { AppIcon } from "./Generic/AppIcon";
import { AppLocationModal } from "./Generic/AppLocationModal";
import { AppModalProps, ErrorModal } from "./Generic/AppModal/AppModal";
import { AppSlider } from "./Generic/AppSlider";
import CalendarPicker from "./Generic/CalendarPicker/CalendarPicker";
import { If } from "./Generic/If";
import { isDefined } from "./Generic/IsDefined";
import { OutsourcingAgencyBanner } from "./OutsourcingAgencyBanner/OutsourcingAgencyBanner";
import CallPatientBottomSheet from "./Patient/components/CallPatientBottomSheet";
import DirectionsBottomSheet from "./Patient/components/DirectionsBottomSheet";
import PatientScheduleListItem from "./PatientScheduleListItem";
import { ScheduleTime } from "./PatientTaskInstanceActivity";
import PatientTaskInstanceScreenFooter from "./PatientTaskInstanceScreenFooter";
import PatientTaskRequiredDocumentList from "./PatientTaskRequiredDocumentList";
import PatientTaskScheduleDateTime from "./PatientTaskScheduleDateTime";
import ScaffoldTitle from "./Scaffold/components/ScaffoldTitle";
import Scaffold from "./Scaffold/Scaffold";
import TaskInstanceDetails from "./TaskInstanceDetails";
import NotesCard from "./Patient/components/NotesCard";
import ListCard from "./ListCard";

interface Props {
  patient: Patient;
  patientTaskInstance: PatientTaskInstance;
  scheduleDateTime: LocalDateTime | null;
  isCallPatientOpen: boolean;
  calendarViewYear: number;
  calendarViewMonth: Month;
  scheduleDate: LocalDate | null;
  scheduleTime: ScheduleTime | null;
  isScheduleDateTimePickerOpen: boolean;
  isTimePickerShake: boolean;
  calendarMinDate?: LocalDate;
  isLocationModalOpen: boolean;
  isDirectionsOpen: boolean;
  errorModalProps: Omit<AppModalProps, "type">;
  caregiverGeoLocation: Geo;
  minMinutes?: number;
  maxMinutes?: number;
  canClockIn: boolean;
  canClockOut: boolean;
  canSubmitTimesheet: boolean;
  nearFutureCaregivers: NearFutureCaregiver[];
  onClickClockIn: () => void;
  onClickClockOut: () => void;
  onClickSubmitTimesheet: () => void;
  onTimePickerShakeAnimationEnd: () => void;
  onClickOpenCallPatient: () => void;
  onClickCallPatient: (phoneNumber: string) => void;
  onClickOpenScheduleDateTimePicker: () => void;
  onRequestCloseScheduleDateTimePicker: () => void;
  onRequestCloseCallPatient: () => void;
  onRequestChangeCalendarViewYear: (year: number) => void;
  onRequestChangeCalendarViewMonth: (month: Month) => void;
  onRequestChangeScheduleDate: (date: LocalDate) => void;
  onRequestChangeScheduleTime: (time: ScheduleTime) => void;
  onRequestApproveScheduleDateTime: () => void;
  onRequestCloseLocationModal: () => void;
  onRequestCloseDirections: () => void;
  onClickRegularDocument: (documentId: PatientDocumentTypeId) => void;
  onClickScannedDocument: (name: string, pdfUrl: string) => void;
  onClickPlanOfCare: () => void;
  onClickBack: () => void;
  onClickLocationModal: () => void;
  onClickDirections: () => void;
  onClickUnscheduleTask: () => void;
  onClickAddDocument: () => void;
  onClickCall: (params: { name: string; destination: string; agencyId: AgencyId }) => void;
  onRequestNavigate: (page: Page) => void;
  onclickDisabled: () => void;
  onRequestOutsourcingAgencyData: API["getOutsourcingAgencyData"];
  onClickLinkedPatient: () => void;
  onClickCreatePatientNote: (note: string) => void;
}

const MIN_MINUTES = 0;
const MAX_MINUTES = 24 * 60;
const isInsideOfRange = (num: number) => num >= MIN_MINUTES && num <= MAX_MINUTES;

const PatientTaskInstanceScreen = (props: Props) => {
  const minMinutes = props.minMinutes ?? MIN_MINUTES;
  const maxMinutes = props.maxMinutes ?? MAX_MINUTES;
  let fromMinute = MIN_MINUTES;
  let toMinute = MAX_MINUTES;

  const isVisitCheckedIn =
    props.patientTaskInstance.type === "SCHEDULED" &&
    props.patientTaskInstance.visitInstance !== null &&
    (props.patientTaskInstance.visitInstance.checkInTime !== null ||
      isDefined(props.patientTaskInstance.visitInstance.clockInTime));

  if (props.patientTaskInstance.taskDetails.durationMinutes !== null) {
    toMinute = props.patientTaskInstance.taskDetails.durationMinutes;
  }

  if (props.scheduleTime !== null) {
    fromMinute = Math.round(props.scheduleTime.start / 15) * 15;
    toMinute = Math.round(props.scheduleTime.end / 15) * 15;
  }
  const fromAmpmText = minutesToDisplayTime(fromMinute);
  const toAmpmText = minutesToDisplayTime(toMinute);
  const durationText = decimalHourToDisplay((toMinute - fromMinute) / 60, "narrow");

  const handleRequestTimeChange = React.useCallback(
    (value: number | number[] | null | undefined) => {
      const durationMinutes = props.patientTaskInstance.taskDetails.durationMinutes;

      if (!Array.isArray(value) || value.length !== 2) {
        throw new Error(
          `Got invalid value of handleChange in PatientTaskInstanceScreen (${value})`
        );
      }

      if (!isInsideOfRange(value[0]) || !isInsideOfRange(value[1])) {
        return;
      }

      const scheduleTime = ((): ScheduleTime | undefined => {
        if (props.scheduleTime === null) {
          return {
            start: value[0],
            end: value[1],
          };
        }

        if (value[0] !== props.scheduleTime.start) {
          return {
            start: value[0],
            end: durationMinutes !== null ? value[0] + durationMinutes : value[1],
          };
        }

        if (value[1] !== props.scheduleTime.end) {
          return {
            start: durationMinutes !== null ? value[1] - durationMinutes : value[0],
            end: value[1],
          };
        }

        return;
      })();

      if (scheduleTime === undefined) {
        return;
      }

      if (isInsideOfRange(scheduleTime.start) && isInsideOfRange(scheduleTime.end)) {
        return props.onRequestChangeScheduleTime.call(null, scheduleTime);
      }
    },
    [
      props.onRequestChangeScheduleTime,
      props.patientTaskInstance.taskDetails.durationMinutes,
      props.scheduleTime,
    ]
  );

  const handleClickPhone = React.useCallback(
    (phone: string) => {
      props.onClickCallPatient.call(null, phone);
    },
    [props.onClickCallPatient]
  );

  const scheduleDrationMinutes = React.useMemo(() => {
    if (props.scheduleTime === null) {
      return null;
    }

    return props.scheduleTime.end - props.scheduleTime.start;
  }, [props.scheduleTime]);

  const actions = React.useMemo(() => {
    return [
      <AppIcon
        key="directions"
        icon={MAP_MARKER_DUOTONE_ICON}
        size={24}
        onClick={props.onClickLocationModal}
      />,
    ];
  }, [props.onClickLocationModal]);

  const footer = (() => {
    if (
      props.patientTaskInstance.type !== "SCHEDULED" ||
      props.patientTaskInstance.visitInstance === null ||
      (props.patientTaskInstance.visitInstance.clockInTime !== null &&
        props.patientTaskInstance.visitInstance.checkOutTime !== null) ||
      isDefined(props.patientTaskInstance.visitInstance.clockOutTime)
    ) {
      return;
    }

    return (
      <PatientTaskInstanceScreenFooter
        canClockIn={props.canClockIn}
        canClockOut={props.canClockOut}
        canSubmitTimesheet={props.canSubmitTimesheet}
        onClockIn={props.onClickClockIn}
        onClockOut={props.onClickClockOut}
        onSubmitTimesheet={props.onClickSubmitTimesheet}
      />
    );
  })();

  const renderedLocationModal = React.useMemo(() => {
    if (props.patientTaskInstance.taskDetails.patientAddressComponent === null) {
      return null;
    }

    return (
      <AppLocationModal
        isOpen={props.isLocationModalOpen}
        originLocation={{
          lat: props.caregiverGeoLocation.lat,
          lng: props.caregiverGeoLocation.lng,
        }}
        destination={props.patientTaskInstance.taskDetails.patientAddressComponent}
        destinationText={withDefault(
          props.patientTaskInstance.taskDetails.patientAddress,
          props.patientTaskInstance.taskDetails.patientAddress2
        )}
        onClickDirections={props.onClickDirections}
        onRequestClose={props.onRequestCloseLocationModal}
      />
    );
  }, [
    props.caregiverGeoLocation,
    props.isLocationModalOpen,
    props.onClickDirections,
    props.onRequestCloseLocationModal,
    props.patientTaskInstance.taskDetails.patientAddress,
    props.patientTaskInstance.taskDetails.patientAddress2,
    props.patientTaskInstance.taskDetails.patientAddressComponent,
  ]);

  const isTaskOverdue =
    props.patientTaskInstance.scheduleDateTime === null &&
    props.patientTaskInstance.dueDate.isBefore(LocalDate.now());

  const patientContacts = extractPatientContacts(props.patient);
  const caregiversContacts = props.nearFutureCaregivers.flatMap((c) => {
    return c.phoneNumber === null
      ? []
      : [
          {
            name: `${translate(STRINGS.AIDE)} - ${getFullName(c)}`,
            e164: c.phoneNumber,
          },
        ];
  });

  const publicVisitDetails =
    props.patientTaskInstance.type === "SCHEDULED"
      ? props.patientTaskInstance.visitInstance?.publicVisitDetails
      : null;

  const medicalNotes = publicVisitDetails?.patientMedicalNotes ?? null;
  const patientRequests = publicVisitDetails?.patientRequests ?? [];

  return (
    <Scaffold
      title={<ScaffoldTitle label={props.patientTaskInstance.taskDetails.title} />}
      className="PatientTaskInstanceScreen"
      onClickGoBack={props.onClickBack}
      endActions={actions}
      footer={footer}
    >
      <OutsourcingAgencyBanner
        patientId={props.patient.id}
        onRequestOutsourcingAgencyData={props.onRequestOutsourcingAgencyData}
        context="Visit"
      />
      <ScheduleDateContainer>
        <If condition={props.scheduleDateTime === null}>
          <span className="tell-us-when">
            {translate(STRINGS.PLEASE_TELL_US_WHEN_DO_YOU_PLAN_TO_PERFORM_THIS_TASK)}
          </span>
        </If>
        <PatientTaskScheduleDateTime
          dateTime={props.scheduleDateTime}
          durationMinutes={scheduleDrationMinutes}
          onClickOpenChangeDateTime={props.onClickOpenScheduleDateTimePicker}
          onClickUnscheduleTask={props.onClickUnscheduleTask}
          onclickDisabled={props.onclickDisabled}
          isVisitCheckedIn={isVisitCheckedIn}
          visitHasCico={
            props.patientTaskInstance.clockInTime !== null ||
            props.patientTaskInstance.clockOutTime !== null
          }
        />

        {isTaskOverdue && (
          <AlertContainer>
            <Alert type="danger">{translate(STRINGS.THIS_TASK_IS_OVERDUE_SCHEDULE_ASAP)}</Alert>
          </AlertContainer>
        )}

        <AppCard>
          <AppCardContent>
            <PatientScheduleListItem
              onClick={() =>
                props.onRequestNavigate({
                  type: "PatientSchedule",
                  patientTaskInstanceId: props.patientTaskInstance.id,
                })
              }
            />
          </AppCardContent>
        </AppCard>
      </ScheduleDateContainer>

      <TaskInstanceDetails
        onClickCreatePatientNote={props.onClickCreatePatientNote}
        dayTimeParams={props.patientTaskInstance.dayTimeParams}
        startDate={props.patientTaskInstance.startDate}
        dueDate={props.patientTaskInstance.dueDate}
        patientId={props.patient.id}
        durationMinutes={props.patientTaskInstance.taskDetails.durationMinutes}
        patientAddress={props.patientTaskInstance.taskDetails.patientAddress}
        patientAddressComponents={props.patientTaskInstance.taskDetails.patientAddressComponent}
        patientAddressInstructions={
          props.patientTaskInstance.taskDetails.patientAddressInstructions
        }
        patientAge={props.patientTaskInstance.taskDetails.patientAge}
        patientGender={props.patientTaskInstance.taskDetails.patientGender}
        patientName={props.patientTaskInstance.taskDetails.patientName}
        patientTaskInstanceId={props.patientTaskInstance.id}
        onClickLocation={props.onClickLocationModal}
        onRequestNavigate={props.onRequestNavigate}
        linkedPatientName={props.patientTaskInstance.taskDetails.linkedPatientName}
        linkedPatientTaskInstanceId={
          props.patientTaskInstance.taskDetails.linkedPatientTaskInstanceId
        }
        onClickLinkedPatient={props.onClickLinkedPatient}
      />

      <CardMargin>
        <ContactsListCard
          title={translate(STRINGS.PATIENT_CONTACTS)}
          contacts={patientContacts
            .map((con) => ({
              name: getFullName(con),
              e164: con.emergencyPhoneNumber1,
            }))
            .concat(caregiversContacts)}
          onClickCall={(contact) =>
            props.onClickCall({
              name: contact.name,
              destination: contact.e164,
              agencyId: props.patient.agencyId,
            })
          }
        />
      </CardMargin>

      <If
        condition={
          props.patientTaskInstance.taskDetails.documents.length > 0 ||
          props.patientTaskInstance.taskDetails.hasPlanOfCare
        }
      >
        <PatientTaskRequiredDocumentList
          documents={props.patientTaskInstance.taskDetails.documents}
          hasPlanOfCare={props.patientTaskInstance.taskDetails.hasPlanOfCare}
          planOfCareCompleted={props.patientTaskInstance.taskDetails.planOfCareCompleted}
          onClickRegularDocument={props.onClickRegularDocument}
          onClickScannedDocument={props.onClickScannedDocument}
          onClickPlanOfCare={props.onClickPlanOfCare}
          onClickAddDocument={props.onClickAddDocument}
        />
      </If>

      {medicalNotes !== null && (
        <CardMargin>
          <NotesCard title={translate(STRINGS.MEDICAL_INFORMATION)} notes={medicalNotes} />
        </CardMargin>
      )}

      {patientRequests.length > 0 && (
        <CardMargin>
          <ListCard title={translate(STRINGS.PATIENT_REQUESTS)} items={patientRequests} />
        </CardMargin>
      )}

      <CalendarPicker
        title={translate(STRINGS.CHOOSE_DATE_TO_PERFORM_THIS_TASK)}
        header={
          isTaskOverdue && (
            <AlertContainer>
              <Alert type="danger">{translate(STRINGS.THIS_TASK_IS_OVERDUE_SCHEDULE_ASAP)}</Alert>
            </AlertContainer>
          )
        }
        year={props.calendarViewYear}
        month={props.calendarViewMonth}
        selectedDate={props.scheduleDate}
        isOpen={props.isScheduleDateTimePickerOpen}
        okText={translate(STRINGS.APPROVE)}
        minDate={props.calendarMinDate}
        dayTimeParams={props.patientTaskInstance.dayTimeParams}
        onChangeYear={props.onRequestChangeCalendarViewYear}
        onChangeMonth={props.onRequestChangeCalendarViewMonth}
        onChoose={props.onRequestChangeScheduleDate}
        onSubmit={props.onRequestApproveScheduleDateTime}
        onCancel={props.onRequestCloseScheduleDateTimePicker}
      >
        <div
          className={classNames({ "time-picker-shake": props.isTimePickerShake })}
          onAnimationEnd={props.onTimePickerShakeAnimationEnd}
        >
          <div className="availability-hours-text">
            <span className="choose-time">{`${translate(STRINGS.CHOOSE_TIME)}`}</span>
            <span className="hours-badge">{`${fromAmpmText} - ${toAmpmText}`}</span>
          </div>
          <div className="availability-duration-text">
            <span className="task-duration">{translate(STRINGS.DURATION)}</span>
            <span className="duration-badge">{durationText}</span>
          </div>
          <AppSlider
            withBars={true}
            startHandler={{ direction: "start", children: fromAmpmText }}
            endHandler={{ direction: "end", children: toAmpmText }}
            min={minMinutes}
            max={maxMinutes}
            step={15}
            snapDragDisabled={true}
            value={[fromMinute, toMinute]}
            onChange={handleRequestTimeChange}
          />
        </div>
      </CalendarPicker>
      <CallPatientBottomSheet
        isOpen={props.isCallPatientOpen}
        homePhone={props.patientTaskInstance.taskDetails.patientHomePhoneNumber}
        mobilePhone={props.patientTaskInstance.taskDetails.patientMobilePhoneNumber}
        onClickPhone={handleClickPhone}
        onRequestClose={props.onRequestCloseCallPatient}
      />
      <ErrorModal {...props.errorModalProps} />
      {renderedLocationModal}
      <DirectionsBottomSheet
        isOpen={props.isDirectionsOpen}
        destination={withDefault(
          props.patientTaskInstance.taskDetails.patientAddress,
          props.patientTaskInstance.taskDetails.patientAddress2
        )}
        onRequestClose={props.onRequestCloseDirections}
      />
    </Scaffold>
  );
};

const ScheduleDateContainer = styled.div`
  margin-bottom: 1rem;
`;

const AlertContainer = styled.div`
  margin: 1rem 0;
`;

const CardMargin = styled.div`
  margin: 16px 0;
`;

export default React.memo(PatientTaskInstanceScreen);
